import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import Loader from '@Components/Loader';
import { Button } from '@els/els-react--button';
import HoverTooltip from '@Components/HoverTooltip';
import { TooltipConstants } from '@Components/Tooltip';
import { isAPIError } from '@Containers/CasNumber/utils';
import InnerHTML from '@Components/InnerHTML';
import { onCtaMounted } from '@Containers/CasNumber/analytics';
import { getAutoFetchedCount, incrementAutoFetchedCount } from '@Containers/CasNumber/autoFetchForCasSearch';

import './CasNumber.scss';

function CasNumberCta({ structure, name, inChiKey, substanceId, databaseId, fetchCasNumber }) {
  useEffect(() => {
    onCtaMounted({ substanceId, databaseId });
  }, []);

  return (
    // Update to use ButtonWithIcon when CasNumber icon when available
    <Button
      type="link"
      onClick={() => fetchCasNumber({ structure, name, inChiKey, substanceId, databaseId, withCta: true })}
      aria-label="Retrieve CAS RN"
      data-e2e="retrieve-cas-number"
      className="e2e-retrieve-cas-number">
      <svg className="cas-number__icon svg-icon" aria-hidden="true">
        <use xlinkHref="#icon-cas-number" />
      </svg>
      Retrieve CAS RN
    </Button>
  );
}

// eslint-disable-next-line react/prop-types
function CasNumberContent({ casNumber, fromCasApi, showTooltip = true }) {
  const tooltip = fromCasApi ? 'Retrieved CAS Registry Number' : 'CAS Registry Number';
  const className = cn('cas-number__content', { 'cas-number__content--from-api': fromCasApi });
  const content = showTooltip ? (
    // Adding tabindex to make it focusable for screen readers to read the tooltip, as there is no interactive element inside to trigger the tooltip.
    // The tooltip will be read on focus and hover.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    <span className="rx-focusable" tabIndex="0">
      {fromCasApi && (
        <svg className="cas-number__icon svg-icon">
          <use xlinkHref="#icon-cas-number" />
        </svg>
      )}
      <InnerHTML tag="span" html={casNumber} />
    </span>
  ) : (
    <>
      {fromCasApi && (
        <svg className="cas-number__icon svg-icon">
          <use xlinkHref="#icon-cas-number" />
        </svg>
      )}
      <InnerHTML tag="span" html={casNumber} />
    </>
  );

  return showTooltip ? (
    <HoverTooltip className={className} theme="simple" tooltipPlace="top-start" id={TooltipConstants.ids.GlobalHover} tooltip={tooltip}>
      {content}
    </HoverTooltip>
  ) : (
    <span className={className}>{content}</span>
  );
}

function CasErrorContent() {
  return (
    <span className="cas-number__error">
      <svg className="cas-number__icon svg-icon svg-icon">
        <use xlinkHref="#icon-cas-number" />
      </svg>
      CAS RN Unavailable
    </span>
  );
}

function CasNumber({
  className,
  substanceId,
  databaseId,
  resultName,
  structure,
  name,
  inChiKey,
  withCta = true,
  isCasNumberRetrievable,
  fetchCasNumber = () => undefined,
  casNumberLoading,
  casNumberError,
  casNumber,
  fromCasApi,
  isFromCasSearch,
  showTooltip = true,
}) {
  const showError = casNumberError && (!isAPIError(casNumberError) || !withCta);

  useEffect(() => {
    if (casNumber || !isCasNumberRetrievable || (casNumberError && !isAPIError(casNumberError))) {
      return;
    }

    if (!withCta) {
      // Should fetch automatically if there is no CTA - substance details modal
      fetchCasNumber({ structure, name, inChiKey, substanceId, databaseId, withCta });
    } else if (isFromCasSearch && getAutoFetchedCount({ resultName }) < 5) {
      // Should fetch automatically first 5 CAS numbers if from CAS search
      fetchCasNumber({ structure, name, inChiKey, substanceId, databaseId, withCta });
      incrementAutoFetchedCount({ resultName });
    }
  }, []);

  return (
    <div className={cn('cas-number', className)}>
      {casNumber ? (
        <CasNumberContent casNumber={casNumber} fromCasApi={fromCasApi} showTooltip={showTooltip} />
      ) : (
        isCasNumberRetrievable && (
          <>
            {casNumberLoading && <Loader className="cas-number__loader" showText={false} startProgress={50} />}
            {showError && <CasErrorContent />}
            {!casNumberLoading && !showError && withCta && (
              <CasNumberCta
                structure={structure}
                name={name}
                inChiKey={inChiKey}
                substanceId={substanceId}
                databaseId={databaseId}
                fetchCasNumber={fetchCasNumber}
              />
            )}
          </>
        )
      )}
    </div>
  );
}

CasNumber.propTypes = {
  className: PropTypes.string,
  substanceId: PropTypes.string,
  databaseId: PropTypes.string,
  resultName: PropTypes.string,
  structure: PropTypes.string,
  name: PropTypes.string,
  inChiKey: PropTypes.string,
  withCta: PropTypes.bool,
  isCasNumberRetrievable: PropTypes.bool,
  fetchCasNumber: PropTypes.func,
  casNumberLoading: PropTypes.bool,
  casNumberError: PropTypes.number,
  casNumber: PropTypes.string,
  fromCasApi: PropTypes.bool,
  isFromCasSearch: PropTypes.bool,
  showTooltip: PropTypes.bool,
};

export default CasNumber;
